import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, Sofa, Leaf, MapPin, Calendar, AlertCircle, Loader2 } from "lucide-react";
import { ESTADO_COLORS } from "@/mock";
import { trackSolicitud } from "@/api";

export default function TrackTicket() {
  const [ticketCode, setTicketCode] = useState("");
  const [contact, setContact] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const found = await trackSolicitud(ticketCode.trim(), contact.trim());
      setResult(found);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-32 pb-20 px-4 bg-gray-50 min-h-screen">
        <div className="max-w-lg mx-auto">
          <h1 className="font-heading font-bold text-3xl text-municipal mb-2 text-center">Consultar mi solicitud</h1>
          <p className="text-gray-500 text-center mb-10">
            Introduce tu código de ticket y el teléfono o email usado en la solicitud.
          </p>

          <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-md p-6 sm:p-8 space-y-5 mb-8">
            <div>
              <Label className="mb-1.5 block">Código de ticket</Label>
              <Input
                value={ticketCode}
                onChange={(e) => setTicketCode(e.target.value)}
                placeholder="SFH-20260610-1042"
                data-testid="track-ticket-input"
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Teléfono o email</Label>
              <Input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="+34600000000 o correo@ejemplo.com"
                data-testid="track-contact-input"
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="track-search-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Consultar
            </Button>
          </form>

          {notFound && (
            <div className="flex items-center gap-3 bg-red-50 text-red-600 rounded-xl p-4 text-sm" data-testid="track-not-found">
              <AlertCircle className="w-5 h-5 shrink-0" />
              No hemos encontrado ninguna solicitud con esos datos. Revisa el código e inténtalo de nuevo.
            </div>
          )}

          {result && (
            <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8" data-testid="track-result-card">
              <div className="flex items-center justify-between mb-5">
                <span className="font-heading font-bold text-lg text-municipal">{result.ticket_code}</span>
                <Badge className={ESTADO_COLORS[result.estado] || "bg-gray-100 text-gray-700"}>{result.estado}</Badge>
              </div>
              <div className="space-y-3 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  {result.tipo_servicio === "enseres" ? <Sofa className="w-4 h-4 text-municipal" /> : <Leaf className="w-4 h-4 text-valoriza-green" />}
                  Servicio: <strong className="text-municipal">{result.tipo_servicio === "enseres" ? "Enseres" : "Poda"}</strong>
                </p>
                <p className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-dorado" /> Fecha preferida: <strong className="text-municipal">{result.fecha_preferida}</strong>
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-dorado" /> {result.direccion_completa}
                </p>
              </div>
            </div>
          )}

          <p className="text-center text-sm text-gray-400 mt-8">
            ¿No tienes ticket? <Link to="/solicitud" className="text-municipal font-medium hover:text-dorado">Crea una nueva solicitud</Link>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
